/**
 * Keyboard-event helpers for global shortcuts.
 *
 * Single concern for now: detect when a printable-character shortcut
 * would interfere with the user typing. Modifier-keyed shortcuts
 * (Cmd+K, Cmd+B) don't need this check — printable shortcuts (`?`,
 * `n`, `/`) do.
 */

/** Subset of `HTMLElement` that the typing-target predicate inspects.
 *  Pure shape so the rule is unit-testable without jsdom (lexide's
 *  Vitest convention is `environment: "node"`). */
export interface TypingTargetShape {
  readonly tagName?: string;
  readonly isContentEditable?: boolean;
}

/**
 * True for elements where the user is plausibly typing — `<input>`,
 * `<textarea>`, `<select>`, or anything `contenteditable=true`.
 *
 * Single-character global shortcuts (e.g. `?`) MUST consult this so
 * they don't steal keystrokes from form fields. Modifier shortcuts
 * (Cmd+K, Cmd+B) bypass it intentionally — Cmd+K should still open
 * the palette even when focus is in a textarea.
 */
export function isTypingElement(target: TypingTargetShape | null | undefined): boolean {
  if (!target) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return target.isContentEditable === true;
}

/**
 * `KeyboardEvent`-shaped wrapper around `isTypingElement`. Returns
 * false when the target isn't an `HTMLElement` (window/document
 * targets fire before focus settles — don't suppress shortcuts then).
 *
 * Exercised by Playwright; the pure rule above is exercised by Vitest.
 */
export function isTypingTarget(e: KeyboardEvent): boolean {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return false;
  return isTypingElement(target);
}
